﻿import {Link, Navigate, useLocation, useSearchParams} from 'react-router-dom';
import {useForm} from 'react-hook-form';
import {FormTextField} from '../../ui/forms/input-field/text-field/text-field';
import {FormSelect, Option} from '../../ui/forms/select/select';
import {Button} from '../../ui/buttons/button';
import {Form} from '../../ui/forms/form';
import {LinkStyle} from '../../ui/buttons/external-link';
import {RegisterPayload, useRegister} from '../requests/use-register';
import {SocialAuthSection} from './social-auth-section';
import {AuthLayout} from './auth-layout/auth-layout';
import {Trans} from '../../i18n/trans';
import {FormCheckbox} from '../../ui/forms/toggle/checkbox';
import {CustomMenuItem} from '../../menus/custom-menu';
import {useRecaptcha} from '../../recaptcha/use-recaptcha';
import {StaticPageTitle} from '../../seo/static-page-title';
import {useSettings} from '../../core/settings/use-settings';
import {useContext, useState} from 'react';
import {SiteConfigContext} from '@common/core/settings/site-config-context';

const ROLE_OPTIONS = [
  'Freelance',
  'Revendeur / agence',
  'Commerçant / petite entreprise',
  'Association',
  'Restaurant / café',
  'Coach / consultant',
  'Artisan / services',
  'Autre',
];

export function RegisterPage() {
  const {
    branding,
    registration: {disable},
    social,
  } = useSettings();
  const {auth} = useContext(SiteConfigContext);
  const {verify, isVerifying} = useRecaptcha('register');

  const {pathname} = useLocation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);

  const isWorkspaceRegister = pathname.includes('workspace');
  const isBillingRegister = searchParams.get('redirectFrom') === 'pricing';
  const searchParamsEmail = searchParams.get('email') || undefined;

  const form = useForm<RegisterPayload>({
    defaultValues: {email: searchParamsEmail, onboarding_role: ''},
  });
  const register = useRegister(form, {
    useRegistrationRedirect: !isWorkspaceRegister,
  });
  const validateProfileStep = () => {
    const fields: (keyof RegisterPayload)[] = [
      'first_name',
      'last_name',
      'phone',
      'onboarding_role',
    ];
    const values = form.getValues();
    let isValid = true;

    fields.forEach(field => {
      if (!`${values[field] || ''}`.trim()) {
        form.setError(field, {
          type: 'required',
          message: 'Ce champ est obligatoire.',
        });
        isValid = false;
      }
    });

    if (isValid) {
      form.clearErrors(fields);
    }

    return isValid;
  };

  if (disable) {
    return <Navigate to="/login" replace />;
  }

  let heading = <Trans message="Create a new account" />;
  if (isWorkspaceRegister) {
    heading = (
      <Trans
        values={{siteName: branding?.site_name}}
        message="To join your team on :siteName, create an account"
      />
    );
  } else if (isBillingRegister) {
    heading = <Trans message="First, let's create your account" />;
  }

  const message = (
    <Trans
      values={{
        a: parts => (
          <Link className={LinkStyle} to="/login">
            {parts}
          </Link>
        ),
      }}
      message="Already have an account? <a>Sign in.</a>"
    />
  );

  return (
    <AuthLayout heading={heading} message={message}>
      <StaticPageTitle>
        <Trans message="Register" />
      </StaticPageTitle>
      <Form
        form={form}
        onSubmit={async payload => {
          if (step === 1) {
            if (validateProfileStep()) {
              setStep(2);
            }
            return;
          }

          const isValid = await verify();
          if (isValid) {
            register.mutate(payload);
          }
        }}
      >
        <SignupProgress step={step} />

        {step === 1 ? (
          <>
            <div className="mb-24">
              <div className="text-sm font-medium">
                <Trans message="Vos informations" />
              </div>
              <div className="mt-4 text-xs text-muted">
                <Trans message="On prépare votre espace et on adapte l'accompagnement à votre activité." />
              </div>
            </div>
            <div className="mb-24 grid grid-cols-1 gap-16 md:grid-cols-2">
              <FormTextField
                name="first_name"
                label={<Trans message="Prénom" />}
                autoComplete="given-name"
                required
              />
              <FormTextField
                name="last_name"
                label={<Trans message="Nom" />}
                autoComplete="family-name"
                required
              />
            </div>
            <FormTextField
              className="mb-24"
              name="phone"
              type="tel"
              label={<Trans message="Téléphone" />}
              description={
                <Trans message="Utilisé seulement pour vous accompagner, pas de spam." />
              }
              autoComplete="tel"
              required
            />
            <FormSelect
              className="mb-32"
              name="onboarding_role"
              selectionMode="single"
              label={<Trans message="Vous êtes ?" />}
              required
            >
              {ROLE_OPTIONS.map(option => (
                <Option key={option} value={option}>
                  {option}
                </Option>
              ))}
            </FormSelect>
            <Button
              className="block w-full"
              type="submit"
              variant="flat"
              color="primary"
              size="md"
            >
              <Trans message="Continuer" />
            </Button>
          </>
        ) : (
          <>
            <div className="mb-24">
              <div className="text-sm font-medium">
                <Trans message="Créer vos accès" />
              </div>
              <div className="mt-4 text-xs text-muted">
                <Trans message="Encore quelques secondes, puis vous pourrez choisir un modèle." />
              </div>
            </div>
            <FormTextField
              className="mb-24"
              name="email"
              type="email"
              disabled={!!searchParamsEmail}
              label={<Trans message="Email" />}
              required
            />
            <FormTextField
              className="mb-24"
              name="password"
              type="password"
              label={<Trans message="Password" />}
              required
            />
            <FormTextField
              className="mb-32"
              name="password_confirmation"
              type="password"
              label={<Trans message="Confirm password" />}
              required
            />
            {auth?.registerFields ? <auth.registerFields /> : null}
            <PolicyCheckboxes />
            <div className="flex flex-col-reverse gap-12 sm:flex-row">
              <Button
                className="sm:flex-1"
                type="button"
                variant="outline"
                size="md"
                onClick={() => setStep(1)}
              >
                <Trans message="Retour" />
              </Button>
              <Button
                className="sm:flex-1"
                type="submit"
                variant="flat"
                color="primary"
                size="md"
                disabled={register.isPending || isVerifying}
              >
                <Trans message="Create account" />
              </Button>
            </div>
            <SocialAuthSection
              redirectAfterAuth={isWorkspaceRegister ? 'login' : 'register'}
              dividerMessage={
                social.compact_buttons ? (
                  <Trans message="Or sign up with" />
                ) : (
                  <Trans message="OR" />
                )
              }
            />
          </>
        )}
      </Form>
    </AuthLayout>
  );
}

interface SignupProgressProps {
  step: number;
}

function SignupProgress({step}: SignupProgressProps) {
  return (
    <div className="mb-28">
      <div className="mb-10 flex items-center justify-between text-xs font-medium text-muted">
        <Trans message="Inscription" />
        <span>
          <Trans message="Étape :step sur 2" values={{step}} />
        </span>
      </div>
      <div className="grid grid-cols-2 gap-6">
        {[1, 2].map(item => (
          <div
            key={item}
            className={
              item <= step
                ? 'h-4 rounded-full bg-primary'
                : 'h-4 rounded-full bg-chip'
            }
          />
        ))}
      </div>
    </div>
  );
}

function PolicyCheckboxes() {
  const {
    registration: {policies},
  } = useSettings();

  if (!policies) return null;

  return (
    <div className="mb-32">
      {policies.map(policy => (
        <FormCheckbox
          key={policy.id}
          name={policy.id}
          className="mb-4 block"
          required
        >
          <Trans
            message="I accept the :name"
            values={{
              name: (
                <CustomMenuItem
                  unstyled
                  className={() => LinkStyle}
                  item={policy}
                />
              ),
            }}
          />
        </FormCheckbox>
      ))}
    </div>
  );
}
